<script setup>
import { ref, computed, watch } from 'vue'
import { useUserStore } from '../stores/userStore'

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  show: Boolean
})

const emit = defineEmits(['close', 'update'])

const userStore = useUserStore()
const selectedRole = ref(props.user.role)
const selectedPackage = ref(props.user.subscription?.package || 'basic')
const apiAccess = ref(props.user.apiAccess?.enabled || false)
const selectedLeagues = ref(props.user.leagueAccess || [])

const save = () => {
  userStore.updateUserRole(props.user.id, selectedRole.value)
  userStore.updateUserSubscription(props.user.id, selectedPackage.value)
  emit('update')
  emit('close')
}

const availableLeagues = computed(() => {
  const pkg = userStore.packages.find(p => p.id === selectedPackage.value)
  return pkg ? pkg.leagueAccess : []
})

watch(selectedPackage, (newPackage) => {
  if (newPackage) {
    const pkg = userStore.packages.find(p => p.id === newPackage)
    if (pkg) {
      props.user.leagueAccess.forEach(league => {
        league.access = pkg.leagueAccess.includes(league.id)
      })
    }
  }
})

const handleToggleClick = (league) => {
  userStore.toggleLeagueAccess(props.user.id, league.id)
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg w-[800px] max-h-[90vh] overflow-y-auto">
      <div class="p-6 border-b">
        <h2 class="text-xl font-semibold">Edit User Access</h2>
      </div>

      <div class="p-6">
        <!-- Role Selection -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">Role</label>
          <select 
            v-model="selectedRole"
            class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
          >
            <option v-for="role in userStore.roles" :key="role.id" :value="role.id">
              {{ role.name }}
            </option>
          </select>
        </div>

        <!-- Package Selection -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">Subscription Package</label>
          <div class="grid grid-cols-2 gap-4">
            <div 
              v-for="pkg in userStore.packages" 
              :key="pkg.id"
              class="border rounded-lg p-4 cursor-pointer"
              :class="selectedPackage === pkg.id ? 'border-pink-500 bg-pink-50' : ''"
              @click="selectedPackage = pkg.id"
            >
              <div class="flex justify-between items-center mb-2">
                <h3 class="font-semibold">{{ pkg.name }}</h3>
                <span class="text-lg font-bold">${{ pkg.price }}</span>
              </div>
              <ul class="text-sm text-gray-600 space-y-1">
                <li v-for="feature in pkg.features" :key="feature" class="flex items-center">
                  <i class="fas fa-check text-green-500 mr-2"></i>
                  {{ feature }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- API Access -->
        <div class="mb-6">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-gray-700">API Access</label>
            <div class="relative inline-block w-12 h-6">
              <input 
                type="checkbox" 
                v-model="apiAccess"
                class="sr-only"
              >
              <div 
                class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer"
                :class="apiAccess ? 'bg-pink-500' : ''"
              ></div>
              <div 
                class="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform"
                :class="apiAccess ? 'transform translate-x-6' : ''"
              ></div>
            </div>
          </div>
          
          <div v-if="apiAccess" class="mt-4 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Rate Limit</label>
              <input 
                type="number" 
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                placeholder="Requests per minute"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">IP Whitelist</label>
              <input 
                type="text" 
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                placeholder="Comma-separated IP addresses"
              >
            </div>
          </div>
        </div>

        <!-- League Access -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">League Access</label>
          <div class="space-y-2 max-h-60 overflow-y-auto">
            <div 
              v-for="league in props.user.leagueAccess" 
              :key="league.id"
              class="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
              @click="handleToggleClick(league)"
            >
              <span>{{ league.name }}</span>
              <div class="relative inline-block w-12 h-6">
                <input 
                  type="checkbox" 
                  v-model="league.access"
                  class="sr-only peer"
                  @change="userStore.toggleLeagueAccess(props.user.id, league.id)"
                >
                <div 
                  class="toggle-bg block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                         transition-colors duration-200 ease-in-out
                         peer-checked:bg-pink-500"
                ></div>
                <div 
                  class="toggle-dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow
                         transition-transform duration-200 ease-in-out
                         peer-checked:translate-x-6"
                ></div>
              </div>
            </div>
          </div>
          <p class="mt-2 text-sm text-gray-500">
            Note: League access may be limited by subscription package
          </p>
        </div>
      </div>

      <div class="p-6 border-t bg-gray-50 flex justify-end gap-3">
        <button 
          @click="$emit('close')"
          class="px-4 py-2 border rounded-lg hover:bg-gray-100"
        >
          Cancel
        </button>
        <button 
          @click="save"
          class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template> 

<style scoped>
/* Improved toggle button styles */
.toggle-bg {
  position: relative;
}

.toggle-bg:before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 9999px;
  background-color: transparent;
  transition: background-color 0.2s;
}

.toggle-bg:hover:before {
  background-color: rgba(236, 72, 153, 0.1);
}

.toggle-dot {
  transform: translateX(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Add ripple effect on click */
@keyframes ripple {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
}

.toggle-bg:active:before {
  animation: ripple 0.5s ease-out;
}

/* Improve hover states */
.cursor-pointer:hover {
  background-color: rgba(243, 244, 246, 0.8);
}

/* Smooth transitions */
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.transition-colors {
  transition-property: background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

/* Focus styles */
input:focus + .toggle-bg {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #EC4899;
}

/* Active state enhancements */
.peer:checked + .toggle-bg {
  background-color: #EC4899;
}

.peer:checked + .toggle-bg .toggle-dot {
  transform: translateX(100%);
}

/* Disabled state */
.peer:disabled + .toggle-bg {
  opacity: 0.5;
  cursor: not-allowed;
}
</style> 